/**
 * Environment type of the mission form: rural or urban only — a controlled ground area is a declaration
 * with a reason on the mission detail, never an environment type. With the automatic environment type
 * (auto_environment_type of GET /missions/flight-area/settings) the server sets it from the ŘLP zones on
 * save, so the field says so and keeps the select only as the fallback for undetermined zone facts. Until
 * the settings load (or when they fail) the mode is unknown: the select stays with a hint.
 */
import { Label } from "../ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { fillTemplate } from "../../lib/arcDisplay";

/** `auto`, `manual` or `unknown` (settings not loaded) — never `manual` before the server said so. */
export const environmentFieldMode = (settings) => {
  if (!settings) return "unknown";
  return settings.auto_environment_type === true ? "auto" : "manual";
};

export const MissionEnvironmentField = ({ settings, value, onChange, storedEnvironmentType, t = {} }) => {
  const mode = environmentFieldMode(settings);
  const environmentLabel = (environment) => (environment === "urban" ? t.sora?.urban : t.sora?.rural) || environment;

  return (
    <div className="space-y-2">
      <Label className="text-slate-600">{t.sora?.environment || "Environment Type"}</Label>
      {mode === "auto" && (
        <>
          <div className="rounded-md border border-slate-200 bg-slate-50 p-3 space-y-1" data-testid="mission-environment-auto">
            <p className="text-sm font-medium text-slate-900">{t.arcDisplay?.environmentAutoTitle}</p>
            {storedEnvironmentType && (
              <p className="text-xs text-slate-600">
                {fillTemplate(t.arcDisplay?.environmentAutoStored, { value: environmentLabel(storedEnvironmentType) })}
              </p>
            )}
            <p className="text-xs" style={{ color: 'var(--text-muted)' }}>{t.arcDisplay?.environmentAutoHelp}</p>
          </div>
          <p className="text-xs text-slate-600">{t.arcDisplay?.environmentAutoFallbackLabel}</p>
        </>
      )}
      <Select value={value} onValueChange={onChange}>
        <SelectTrigger data-testid="mission-environment" className="bg-white border-slate-300 text-slate-900">
          <SelectValue />
        </SelectTrigger>
        <SelectContent className="bg-white border-slate-200 shadow-lg">
          <SelectItem value="rural" className="text-slate-900">{t.sora?.rural || "Rural / Sparsely Populated"}</SelectItem>
          <SelectItem value="urban" className="text-slate-900">{t.sora?.urban || "Urban / Populated"}</SelectItem>
        </SelectContent>
      </Select>
      {mode === "manual" && (
        <p className="text-xs" style={{ color: 'var(--text-muted)' }}>{t.arcDisplay?.environmentManualHelp}</p>
      )}
      {mode === "unknown" && (
        <p className="text-xs text-amber-700" data-testid="mission-environment-unknown">{t.arcDisplay?.environmentSettingsUnknown}</p>
      )}
    </div>
  );
};
